import React, { useState } from 'react'
import { toPng } from 'html-to-image'
import { X, Link, Code, Download, Loader2 } from 'lucide-react'

type ShareTab = 'link' | 'embed' | 'download'

interface ShareModalProps {
  slug: string
  canvasRef: React.RefObject<HTMLDivElement | null>
  onClose: () => void
}

export default function ShareModal({ slug, canvasRef, onClose }: ShareModalProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ShareTab>('link')
  const [downloading, setDownloading] = useState(false)
  const fullUrl = `${window.location.origin}/d/${slug}`
  const embedUrl = `${window.location.origin}/embed/${slug}`
  const iframeCode = `<iframe id="explaina-${slug}" src="${embedUrl}?ref=YOUR_SITE" width="100%" height="600" style="border:none;border-radius:12px" loading="lazy"></iframe>
<script>window.addEventListener("message",function(e){if(e.data&&e.data.type==="explaina-resize"&&e.data.src&&e.data.src.indexOf("${slug}")!==-1){document.getElementById("explaina-${slug}").style.height=e.data.height+"px"}});</script>`
  const pixelCode = `<img src="${window.location.origin}/p/${slug}.gif" width="1" height="1" alt="" style="position:absolute;opacity:0" />`

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const handleDownloadScreenshot = async () => {
    if (!canvasRef.current) return
    setDownloading(true)
    try {
      const dataUrl = await toPng(canvasRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: true,
      })
      const link = document.createElement('a')
      link.download = `explaina-${slug}.png`
      link.href = dataUrl
      link.click()
    } catch (e) {
      console.error('Screenshot failed:', e)
    } finally {
      setDownloading(false)
    }
  }

  const tabs: { key: ShareTab; label: string; icon: React.ReactNode }[] = [
    { key: 'link', label: 'Link', icon: <Link className="h-4 w-4" /> },
    { key: 'embed', label: 'Embed', icon: <Code className="h-4 w-4" /> },
    { key: 'download', label: 'Download', icon: <Download className="h-4 w-4" /> },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <h2 className="text-lg font-bold text-slate-900">Share & Export</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-[#5048e5]/10 text-[#5048e5]'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {activeTab === 'link' && (
            <>
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Direct Link</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={fullUrl}
                    className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-600"
                  />
                  <button
                    onClick={() => copyToClipboard(fullUrl, 'link')}
                    className="shrink-0 px-3 py-2 bg-[#5048e5] text-white rounded-lg text-sm font-medium hover:bg-[#5048e5]/90 transition-colors"
                  >
                    {copiedField === 'link' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-slate-400">
                Share this link with anyone to let them view your interactive demo.
              </p>
            </>
          )}

          {activeTab === 'embed' && (
            <>
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Iframe Embed</label>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-200 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                    {iframeCode}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(iframeCode, 'iframe')}
                    className="absolute top-2 right-2 px-2 py-1 bg-slate-700 text-slate-200 rounded text-[10px] font-medium hover:bg-slate-600 transition-colors"
                  >
                    {copiedField === 'iframe' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400">
                  Replace <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-600">YOUR_SITE</code> with your domain to track referrers in analytics.
                </p>
              </div>

              <div className="space-y-2 pt-2 border-t border-slate-100">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Tracking Pixel</label>
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-200 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all">
                    {pixelCode}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(pixelCode, 'pixel')}
                    className="absolute top-2 right-2 px-2 py-1 bg-slate-700 text-slate-200 rounded text-[10px] font-medium hover:bg-slate-600 transition-colors"
                  >
                    {copiedField === 'pixel' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-[11px] text-slate-400">
                  Add this invisible pixel to emails or newsletters to track opens.
                </p>
              </div>
            </>
          )}

          {activeTab === 'download' && (
            <>
              <div className="space-y-3">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Screenshot</label>
                <p className="text-sm text-slate-600">
                  Download a high-resolution PNG of your demo as it currently appears on the canvas.
                </p>
                <button
                  onClick={handleDownloadScreenshot}
                  disabled={downloading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#5048e5] text-white rounded-lg text-sm font-bold hover:bg-[#5048e5]/90 transition-all disabled:opacity-60"
                >
                  {downloading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      Download PNG
                    </>
                  )}
                </button>
              </div>
              <p className="text-[11px] text-slate-400">
                Use this image for social sharing, thumbnails, or email previews.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
